import React from "react";
import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IMPORT_DESTINATION_FIELDS, IMPORT_FIELD_BY_VALUE } from "./importFields";

function SampleValues({ values = [] }) {
  if (!values.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
        Sin valores de muestra
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {values.map((value, index) => (
        <Chip
          key={`${value}-${index}`}
          size="small"
          variant="outlined"
          label={value}
          title={value}
          sx={{
            maxWidth: 180,
            "& .MuiChip-label": {
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      ))}
    </Stack>
  );
}

export default function ImportMappingStep({
  headers,
  mapping,
  automaticSuggestions,
  columnSamples,
  contract,
  onChange,
}) {
  const mappedValues = Object.values(mapping || {}).filter((value) => value && value !== "ignore");
  const nameMapped = mappedValues.includes("name");
  const backendFields = contract?.destinationFields;
  const supportedFields = backendFields && typeof backendFields === "object"
    ? IMPORT_DESTINATION_FIELDS.filter((field) => Boolean(backendFields[field.value]))
    : IMPORT_DESTINATION_FIELDS;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Mapping de columnas</Typography>
        <Typography variant="body2" color="text.secondary">
          Compará cada columna de tu archivo con ejemplos reales y revisá qué significado tendrá en WebProfit antes de continuar.
        </Typography>
      </Box>

      {!nameMapped ? <Alert severity="warning">Mapeá una columna a <strong>Nombre</strong> para continuar.</Alert> : null}
      <Alert severity="info">
        Las asignaciones automáticas son sugerencias editables. <strong>Stock</strong> se trata como stock inicial/a conciliar y se confirma mediante movimientos ERP.
      </Alert>

      <Stack spacing={1.25}>
        {headers.map((header) => {
          const selected = mapping?.[header] || "ignore";
          const definition = IMPORT_FIELD_BY_VALUE[selected];
          const samples = Array.isArray(columnSamples?.[header]) ? columnSamples[header] : [];
          const isAutomaticSuggestion = automaticSuggestions?.[header] === selected && selected !== "ignore";

          return (
            <Paper key={header} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    md: "repeat(2, minmax(0, 1fr))",
                    lg: "minmax(150px, 0.8fr) minmax(210px, 1.15fr) minmax(220px, 1fr) minmax(220px, 1.2fr)",
                  },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.4 }}>
                    Columna de tu archivo
                  </Typography>
                  <Typography sx={{ fontWeight: 900, overflowWrap: "anywhere" }}>{header}</Typography>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.4 }}>
                    Ejemplos reales
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <SampleValues values={samples} />
                  </Box>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Campo WebProfit"
                    value={selected}
                    onChange={(event) => onChange(header, event.target.value)}
                  >
                    <MenuItem value="ignore">Ignorar</MenuItem>
                    {supportedFields.map((field) => {
                      const usedByOther = Object.entries(mapping || {}).some(
                        ([source, value]) => source !== header && value === field.value,
                      );
                      const backendLabel = backendFields?.[field.value]?.label;
                      return (
                        <MenuItem key={field.value} value={field.value} disabled={usedByOther}>
                          {backendLabel || field.label}{field.required ? " *" : ""}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  {isAutomaticSuggestion ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="Sugerencia automática · revisala"
                      sx={{ mt: 1, maxWidth: "100%" }}
                    />
                  ) : null}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900, lineHeight: 1.4 }}>
                    WebProfit lo interpreta como
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
                    {definition?.warning ? <Chip size="small" color="warning" label="ERP" /> : null}
                    <Typography variant="body2" color="text.secondary">
                      {definition?.description || "Esta columna no se importará."}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
